import { AssociatedObjectsCardContext } from '../../context/AssociatedObjectsCardContext';
import { POLL_INTERVAL, UNKNOWN_ACTION, useFetchAssociatedObjectsCardAssociations } from '../../hooks/useFetchAssociatedObjectsCardAssociations';
import useFetchAssociationTypesData from '../../context/useFetchAssociationTypesData';
import { useFetchAssociatedObjectsCardObject } from '../../hooks/useFetchAssociatedObjectsCardObject';
import AssociatedObjectsCardTitle from './AssociatedObjectsCardTitle';
import { getImpliedUnlabeledAssociationDetails } from '../../hooks/useFetchAssociatedObjectsCardAssociations';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { useOnCrmMessageTopic } from 'crm-message-bus/useOnCrmMessageTopic';
import Raven from 'raven-js';
import { useFetchAllAssociationTypesBetweenObjectIds } from '../../hooks/useFetchAllAssociationTypesBetweenObjectIds';
import { useEffect, useMemo, useRef } from 'react';
import { useSkeletonState } from '../../hooks/useSkeletonState';
import { Metrics } from '../../helpers/Metrics';
import { jsx as _jsx } from "react/jsx-runtime";
function AssociatedObjectsCardTitleDataProvider({
  cardId,
  hasScope,
  isCollapsed,
  isEmbedded,
  isUngated,
  objectId,
  objectTypeId,
  onAssociationUpdate,
  onObjectCreate,
  onObjectUpdate,
  propertyNames,
  propertySource,
  readOnly,
  sourceApp,
  title,
  toObjectTypeId
}) {
  const [skeletonState, setSkeletonState] = useSkeletonState({
    cardId,
    objectId,
    objectTypeId
  });
  const hasReportedSkeletonData = useRef(false);
  const associationTypesData = useFetchAssociationTypesData({
    fromObjectTypeId: objectTypeId,
    toObjectTypeId
  });
  const {
    associationTypes,
    unlabeledAssociationType,
    primaryAssociationType
  } = associationTypesData;
  const {
    impliedUnlabeledAssociationCategory,
    impliedUnlabeledInverseAssociationTypeId
  } = getImpliedUnlabeledAssociationDetails({
    associationTypes,
    unlabeledAssociationType
  });
  const {
    impliedUnlabeledAssociationObjects,
    primaryAssociationObject,
    refetch: refetchAssociationsData,
    startPolling: startPollingAssociationsData,
    onStopPolling: stopPollingAssociationsData,
    updateAssociationAction,
    associationsDataError
  } = useFetchAssociatedObjectsCardAssociations({
    impliedUnlabeledAssociationCategory,
    impliedUnlabeledInverseAssociationTypeId,
    fromObjectId: objectId,
    fromObjectTypeId: objectTypeId,
    toObjectTypeId,
    primaryAssociationCategory: primaryAssociationType && primaryAssociationType.associationCategory,
    primaryInverseAssociationTypeId: primaryAssociationType ? primaryAssociationType.inverseAssociationTypeId : null,
    propertyNames
  });
  const {
    objectTypePermissions,
    primaryDisplayLabel,
    refetch: refetchObjectData,
    userPermissions
  } = useFetchAssociatedObjectsCardObject({
    objectId: Number(objectId),
    objectType: objectTypeId,
    toObjectTypeId
  });
  const toObjectIds = useMemo(() => {
    if (!impliedUnlabeledAssociationObjects) {
      return [];
    }
    const ids = impliedUnlabeledAssociationObjects.results.map(association => association.id);
    if (primaryAssociationObject && primaryAssociationObject.results[0] && !ids.includes(primaryAssociationObject.results[0].id)) {
      ids.push(primaryAssociationObject.results[0].id);
    }
    return ids;
  }, [impliedUnlabeledAssociationObjects, primaryAssociationObject]);
  const {
    refetch: refetchAllAssociationTypesBetweenObjectIdsData
  } = useFetchAllAssociationTypesBetweenObjectIds({
    fetchPolicy: 'cache-and-network',
    objectId,
    objectTypeId,
    skip: isCollapsed,
    toObjectIds,
    toObjectTypeId
  });
  useEffect(() => {
    if (impliedUnlabeledAssociationObjects && (impliedUnlabeledAssociationObjects.total !== (skeletonState === null || skeletonState === void 0 ? void 0 : skeletonState.value) || !hasReportedSkeletonData.current)) {
      if (!hasReportedSkeletonData.current) {
        hasReportedSkeletonData.current = true;
        Metrics.counter('skeleton.loaded', {
          difference: (impliedUnlabeledAssociationObjects.total - ((skeletonState === null || skeletonState === void 0 ? void 0 : skeletonState.value) || 0)).toString(),
          isSkeletonStateUndefined: (skeletonState === undefined).toString()
        }).increment();
      }
      setSkeletonState(impliedUnlabeledAssociationObjects.total);
    }
  }, [impliedUnlabeledAssociationObjects, setSkeletonState, skeletonState]);
  useOnCrmMessageTopic(TOPIC_NAMES.UPDATE_ASSOCIATIONS, ({
    toObjectTypeId: messageToObjectTypeId,
    objectTypeId: messageObjectTypeId,
    objectId: messageObjectId,
    actionType
  }) => {
    const associatedObjectsIds = impliedUnlabeledAssociationObjects && impliedUnlabeledAssociationObjects.results.map(result => result.id) || [];
    const legacyObjectTypeIdLogic = toObjectTypeId === messageObjectTypeId;
    const correctObjectTypeIdLogic = messageObjectTypeId === objectTypeId && (!messageToObjectTypeId || toObjectTypeId === messageToObjectTypeId);
    if ((legacyObjectTypeIdLogic || correctObjectTypeIdLogic) && [objectId, ...associatedObjectsIds].includes(+messageObjectId) && !updateAssociationAction.current) {
      // Association labels are updated "instantly" so we can refetch them directly
      refetchAllAssociationTypesBetweenObjectIdsData().catch(error => {
        Raven.captureException(error);
        throw new Error('No data returned from refetchAllAssociationTypesBetweenObjectIdsData query');
      });
      if (actionType === 'UPDATE_LABELS') {
        return;
      }
      updateAssociationAction.current = actionType || UNKNOWN_ACTION;
      startPollingAssociationsData(POLL_INTERVAL);
      // Stop polling if after 6s to prevent infinite polling
      setTimeout(() => {
        if (updateAssociationAction.current) {
          stopPollingAssociationsData();
        }
      }, 6000);
    }
  });
  useOnCrmMessageTopic(TOPIC_NAMES.UPDATE_OBJECTS, objectData => {
    const {
      objectTypeId: messageObjectTypeId
    } = objectData[0];
    if (messageObjectTypeId === toObjectTypeId) {
      startPollingAssociationsData(POLL_INTERVAL);
      // Stop polling if after 3s to prevent infinite polling
      setTimeout(() => {
        stopPollingAssociationsData();
      }, 3000);
    }
  });
  return /*#__PURE__*/_jsx(AssociatedObjectsCardContext.Provider, {
    value: {
      associationsData: {
        impliedUnlabeledAssociationObjects,
        primaryAssociationObject,
        associationsDataError
      },
      associationTypesData,
      cardId,
      hasScope,
      isEmbedded,
      isUngated,
      objectId,
      objectTypeId,
      objectTypePermissions,
      onAssociationUpdate,
      onObjectCreate,
      onObjectUpdate,
      primaryDisplayLabel,
      propertyNames,
      propertySource,
      readOnly,
      refetchAssociationsData,
      refetchObjectData,
      sourceApp,
      title,
      toObjectTypeId,
      userPermissions
    },
    children: /*#__PURE__*/_jsx(AssociatedObjectsCardTitle, {})
  });
}
export default AssociatedObjectsCardTitleDataProvider;